import { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Product } from '../types/common';

interface CheckboxItem {
    id: string;
    name: string;
    checked: boolean;
}

type CheckboxFabricType = { id?: string; name: string; url: string };
const normalizeSlug = (v?: string | null) => (v ?? '').toString().trim().toLowerCase();

const useFilterByFabricType = (products: Product[], checkboxTypesOfFabric: CheckboxFabricType[]) => {
    const [checkedFilterItems, setCheckedFilterItems] = useState<string[]>([]);
    const { url } = useParams<{ url: string }>();
    const routeProducts = useMemo(() => {
        if (!url || url === 'discounts') return products;
        return products.filter((p) => (p as any)?.category?.url === url);
    }, [products, url]);
    useEffect(() => {
        setCheckedFilterItems([]);
    }, [url]);
    const checkFilterItem = (slug: string) => {
        const s = normalizeSlug(slug);
        setCheckedFilterItems((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
    };

    const getFabricSlug = (p: any) => {
        const ft = p?.fabricType;
        if (ft == null) return '';
        if (typeof ft === 'string') return normalizeSlug(ft);
        return normalizeSlug(ft?.url ?? ft?.slug ?? p?.fabricTypeUrl ?? p?.fabricTypeSlug);
    };
    const productFabricTypeSlugs = useMemo(() => {
        const set = new Set<string>();
        for (const p of routeProducts) {
            const s = getFabricSlug(p);
            if (s) set.add(s);
        }
        return set;
    }, [routeProducts]);

    const checkboxItems: CheckboxItem[] = useMemo(() => {
        return checkboxTypesOfFabric
            .map((fabricType) => {
                const slug = normalizeSlug(fabricType.url);
                if (!slug) return null;
                return { slug, name: fabricType.name };
            })
            .filter(Boolean)
            .filter((b) => productFabricTypeSlugs.has((b as any).slug))
            .map((b) => ({
                id: (b as any).slug,
                name: (b as any).name,
                checked: checkedFilterItems.includes((b as any).slug),
            }));
    }, [checkboxTypesOfFabric, checkedFilterItems, productFabricTypeSlugs]);
    const productsTorender =
        checkedFilterItems.length > 0
            ? products.filter((p) => {
                  const slug = normalizeSlug((p as any)?.fabricType?.url ?? (p as any)?.fabricType?.slug ?? (p as any)?.fabricTypeUrl ?? (p as any)?.fabricTypeSlug);
                  return slug ? checkedFilterItems.includes(slug) : false;
              })
            : products;
    return {
        checkedFilterItems,
        checkFilterItem,
        productsTorender,
        checkboxItems,
    };
};

export default useFilterByFabricType;
